import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero, Reveal } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import {
  BENCHMARK_PROVENANCE,
  GRADED_REPO_ROWS,
  GradedRepoTable,
  MACRO,
  NO_SYNTHETIC_FIXTURES,
  SyntheticSuiteTable,
  VendoredFixtureTable,
  pctMacro,
} from '@/features/benchmarks';
import { localizedPath } from '@/i18n/locale-path';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Public extraction-accuracy benchmark page (launch-readiness B10) at
 * `/benchmarks`.
 *
 * Every figure comes from `@/features/benchmarks` — the SOT that is
 * test-pinned to `packages/cli/src/eval/published-benchmark.json`, which is
 * itself test-pinned to a fresh run of the committed eval harness. The page
 * discloses methodology AND limits (what 100% does and does not mean) — the
 * "never publish untested accuracy claims" rule made load-bearing. The three
 * result tables live in `features/benchmarks/BenchmarkTables.tsx`.
 */

function Headline({
  value,
  labelKey,
  index = 0,
}: {
  value: string;
  labelKey: string;
  index?: number;
}): ReactElement {
  const { t } = useT();
  return (
    <Reveal
      delayMs={Math.min(index, 4) * 60}
      className="flex flex-col gap-1 rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] bg-gradient-to-b from-[color:var(--bd-raise)] to-[color:var(--bd-page)] p-5"
    >
      <span className="text-3xl font-semibold tracking-tight text-[color:var(--bd-ink)]" dir="ltr">
        {value}
      </span>
      <span className="text-sm text-[color:var(--bd-muted)]">{t(labelKey)}</span>
    </Reveal>
  );
}

export function Benchmarks(): ReactElement {
  const { t, locale } = useT();
  useLocalizedSeoMeta({
    title: t('benchmarks.page.title'),
    description: t('benchmarks.page.description'),
    path: '/benchmarks',
  });

  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      <PageHero
        width="default"
        align="start"
        title={t('benchmarks.page.heading')}
        description={t('benchmarks.page.intro')}
      />
      <Container width="default" className="pb-16 pt-2 sm:pb-20">
        <section aria-labelledby="bm-headline" className="mt-10">
          <h2 id="bm-headline" className="sr-only">
            {t('benchmarks.headline.srHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Headline
              index={0}
              value={pctMacro(MACRO.recall)}
              labelKey="benchmarks.headline.recall"
            />
            <Headline
              index={1}
              value={pctMacro(MACRO.precision)}
              labelKey="benchmarks.headline.precision"
            />
            <Headline
              index={2}
              value={String(MACRO.gradedRepoCount)}
              labelKey="benchmarks.headline.repos"
            />
            <Headline
              index={3}
              value={String(MACRO.scannedFrameworks)}
              labelKey="benchmarks.headline.frameworks"
            />
          </div>
          <p className="mt-3 text-xs text-[color:var(--bd-muted)]">
            {t('benchmarks.headline.provenance')}{' '}
            <span dir="ltr">
              {BENCHMARK_PROVENANCE.generatedAt} · {BENCHMARK_PROVENANCE.commit}
            </span>
          </p>
        </section>

        <section aria-labelledby="bm-method" className="mt-10">
          <h2
            id="bm-method"
            className="text-[1.35rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]"
          >
            {t('benchmarks.method.heading')}
          </h2>
          <ul className="mt-4 flex flex-col gap-3">
            {(['truth', 'structural', 'static', 'gates'] as const).map((id) => (
              <li key={id} className="rounded-lg border border-[color:var(--bd-line)] p-4">
                <span className="text-sm font-semibold text-[color:var(--bd-ink)]">
                  {t(`benchmarks.method.${id}.title`)}
                </span>
                <p className="mt-1 text-sm leading-relaxed text-[color:var(--bd-muted)]">
                  {t(`benchmarks.method.${id}.body`)}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="bm-graded" className="mt-10">
          <h2
            id="bm-graded"
            className="text-[1.35rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]"
          >
            {t('benchmarks.graded.heading')}
          </h2>
          {/* The count is DERIVED, not written. This sentence said "Twelve"
              while the table below it rendered 27 rows — in all six locales, on
              the one page whose entire value is that its numbers are checkable.
              Nothing could catch it: key parity sees a translated string, and no
              test compares prose to a data length. A parameter cannot drift. */}
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[color:var(--bd-muted)]">
            {t('benchmarks.graded.intro', { count: String(GRADED_REPO_ROWS.length) })}
          </p>
          <GradedRepoTable />
        </section>

        <section aria-labelledby="bm-synthetic" className="mt-10">
          <h2
            id="bm-synthetic"
            className="text-[1.35rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]"
          >
            {t('benchmarks.synthetic.heading')}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[color:var(--bd-muted)]">
            {t('benchmarks.synthetic.intro')}
          </p>
          <SyntheticSuiteTable />
          {NO_SYNTHETIC_FIXTURES.length > 0 && (
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[color:var(--bd-muted)]">
              {t('benchmarks.synthetic.skipNote')}{' '}
              <span dir="ltr">{NO_SYNTHETIC_FIXTURES.join(', ')}</span>
            </p>
          )}
        </section>

        <section aria-labelledby="bm-vendored" className="mt-10">
          <h2
            id="bm-vendored"
            className="text-[1.35rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]"
          >
            {t('benchmarks.vendored.heading')}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[color:var(--bd-muted)]">
            {t('benchmarks.vendored.intro')}
          </p>
          <VendoredFixtureTable />
        </section>

        <section aria-labelledby="bm-limits" className="mt-10">
          <h2
            id="bm-limits"
            className="text-[1.35rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]"
          >
            {t('benchmarks.limits.heading')}
          </h2>
          <ul className="mt-4 flex max-w-3xl flex-col gap-3">
            {(['curated', 'enrichment', 'auth'] as const).map((id) => (
              <li
                key={id}
                className="flex gap-2 text-sm leading-relaxed text-[color:var(--bd-muted)]"
              >
                <span aria-hidden="true" className="text-[color:var(--bd-ink)]">
                  ·
                </span>
                {t(`benchmarks.limits.${id}`)}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="bm-repro" className="mt-10">
          <h2
            id="bm-repro"
            className="text-[1.35rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]"
          >
            {t('benchmarks.repro.heading')}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[color:var(--bd-muted)]">
            {t('benchmarks.repro.intro')}
          </p>
          {/* Keyboard-pannable — see the same note in `pages/docs/DocBlocks`. */}
          <pre
            dir="ltr"
            tabIndex={0}
            className="mt-4 overflow-x-auto rounded-lg border border-[color:var(--bd-line)] bg-[color:var(--bd-sunk)] p-4 text-sm text-[color:var(--bd-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)]"
          >
            <code>{`${BENCHMARK_PROVENANCE.syntheticCommand}\n${BENCHMARK_PROVENANCE.gradedCommand}`}</code>
          </pre>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-[color:var(--bd-muted)]">
            <Link to={localizedPath('/docs/cli/frameworks', locale)} className={INLINE_LINK}>
              {t('benchmarks.repro.frameworksLink')}
            </Link>{' '}
            {t('benchmarks.repro.outro')}
          </p>
        </section>
      </Container>
    </main>
  );
}
